import { useTranslation } from 'react-i18next';

import Pagination from '@klaviyo-mkt/gatsby-components/molecules/pagination';
import TestimonialsTwoColumn from '@klaviyo-mkt/gatsby-components/organisms/testimonials-two-column';
import { useShouldDisplayBreadcrumbs } from '@klaviyo-mkt/gatsby-theme-marketing/src/hooks/useBreadcrumbs';

import useBlogCategoryQuery from '../../../hooks/useBlogCategoryQuery';
import useNewsfeedQuery from '../../../hooks/useNewsfeedQuery';

import { formatCategoryLink } from '@klaviyo-mkt/gatsby-components/shared/utils/functions';

import { ArchiveBlogCard, ArchiveGrid } from '../styles';
import * as s from './styles';

import type { IArticleArchive  } from './types';

const AllArticlesArchiveLayout = ({ data, pageContext }: IArticleArchive) => {
  const { t, i18n } = useTranslation();
  const { shouldShowBreadcrumbs, homePrefix } = useShouldDisplayBreadcrumbs('/blog', 'default', true, i18n.language);
  const {
    name,
    description,
    categoryOptions: {
      featuredImage,
      testimonial,
    },
  } = data.wpCategory;

  const {
    numberOfPages,
    pageNumber,
    previousPagePath,
    nextPagePath,
  } = pageContext;

  const breadcrumbs = [
    {
      link: homePrefix || '/',
      text: t('breadcrumbs_home'),
    },
    {
      link: `${homePrefix}blog`,
      text: t('breadcrumbs_blog'),
    },
    {
      link: formatCategoryLink(t('langCodes_pathPrefix'), '/blog/category/all-articles'),
      text: name,
    },
  ];

  const blogCategories = useBlogCategoryQuery();
  const newsfeedData = useNewsfeedQuery();
  
  return (
    <>
      { shouldShowBreadcrumbs && <s.CategoryBreadcrumbs breadcrumbs={breadcrumbs} /> }
      <s.CategoryPage>
        <s.CategoryHero
          categoryArchive
          description={description}
          headline={name}
          image={featuredImage}
          layout='two'
        />

        {(pageNumber === 0) && (
          <>
            {testimonial?.quoteText && !testimonial?.quoteText.includes('<p><br data-mce-bogus="1"></p>')  && (
              <TestimonialsTwoColumn
                backgroundColor="light"
                buttons={testimonial.buttons}
                quoteText={testimonial.quoteText}
                {...newsfeedData}
              />
            )}
          </>
        )}

        <ArchiveGrid>
          <s.CategoryCardsTitle
            variant="h3Semibold"
            forwardedAs="h2"
          >
            {`${name} ${t('templates_archive_category_articles')}`}
          </s.CategoryCardsTitle>

          {data.allWpPost.edges.map(({ node }) => {
            const { categories, title, uri: postUri, date, excerpt, author, readTime, templatesEditorial } = node;

            if (categories.nodes[0].link === undefined) {
              return <></>;
            }
            return (
              <ArchiveBlogCard
                key={JSON.stringify(title + postUri + excerpt)}
                title={templatesEditorial?.postTitle?.shortTitle || title}
                categoryName={categories.nodes[0].name}
                categoryLink={categories.nodes[0].link}
                uri={postUri}
                date={date}
                excerpt={excerpt}
                featuredImage={templatesEditorial?.cardImage}
                readTime={readTime}
                authorName={author?.node?.name}
                authorPhoto={author?.node?.usersAuthor?.profilePhoto}
                authorUri={author?.node?.uri}
              />
            );
          })} 

          {(numberOfPages > 1) && (
            <Pagination
              numberOfPages={numberOfPages}
              currentPage={pageNumber}
              baseUrl={formatCategoryLink(t('langCodes_pathPrefix'), '/blog/category/all-articles')}
              previousPagePath={previousPagePath}
              nextPagePath={nextPagePath}
            />
          )}
        </ArchiveGrid>
        <s.Categories customPadding={ { customPaddingBottom: 'md', customPaddingTop: 'md' } } activeCategory={name} categories={blogCategories || []} />
      </s.CategoryPage>
    </>
  );
};

export default AllArticlesArchiveLayout;
